import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import api from '../services/api';
import { getApiUrl } from '../config/runtimeConfig';

import { getAccessToken, clearAuthStorage } from '../services/api';
import Loading from '../components/Loading';
import PrimaryNav from '../components/PrimaryNav';
const GAME_ICONS = { whot: '🎴' };
const GAME_NAMES = { whot: 'WHOT' };
const PLAYER_COLORS = ['#00d4aa', '#ff6b9d', '#ffd166', '#6bcaff'];

const SOCKET_URL = getApiUrl().replace(/\/api$/, '');

export default function RoomsPage() {
  const navigate = useNavigate();
  const token = getAccessToken();

  // UI state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [activeTab, setActiveTab] = useState('public'); // 'public' | 'private'
  const [publicRooms, setPublicRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create form state
  const [createGameType, setCreateGameType] = useState('whot');
  const [createPlayerCount, setCreatePlayerCount] = useState(2);
  const [createTimeLimit, setCreateTimeLimit] = useState(5);
  const [createIsPrivate, setCreateIsPrivate] = useState(true);
  const [createEntryFee, setCreateEntryFee] = useState('');

  // Join private
  const [privateCode, setPrivateCode] = useState('');
  const [joining, setJoining] = useState(false);

  // Created room modal
  const [createdRoom, setCreatedRoom] = useState(null);
  const [copied, setCopied] = useState(false);
  const [creating, setCreating] = useState(false);

  const socketRef = useRef(null);

  // ===================================================
  // FETCH PUBLIC ROOMS
  // ===================================================
  const fetchPublicRooms = useCallback(async () => {
    try {
      setError('');
      const res = await api.get('/rooms/public');
      setPublicRooms(res.data.rooms || []);
    } catch (err) {
      console.error('Fetch rooms error:', err);
      if (err.response?.status !== 401) {
        setError('Failed to load rooms');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    // Auto-refresh every 5s
    const interval = setInterval(fetchPublicRooms, 5000);

    // Socket.io for real-time updates
    socketRef.current = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });

    socketRef.current.on('rooms:newPublic', (data) => {
      setPublicRooms((prev) => [data.room, ...prev].slice(0, 50));
    });

    socketRef.current.on('rooms:statusChange', ({ roomId, status }) => {
      setPublicRooms((prev) =>
        prev.map((r) => (r.id === roomId ? { ...r, status } : r))
      );
    });

    return () => {
      clearInterval(interval);
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, [token, navigate, fetchPublicRooms]);

  // ===================================================
  // CREATE ROOM
  // ===================================================
  async function handleCreate(e) {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      const payload = {
        gameType: createGameType,
        playerCount: createPlayerCount,
        maxDuration: createTimeLimit * 60,
        isPrivate: createIsPrivate,
        entryFee: createEntryFee ? parseFloat(createEntryFee) : 0,
      };

      const res = await api.post('/rooms', payload);
      if (res.data.success) {
        const room = res.data.room;
        // Navigate to waiting room for ALL rooms (both public and private)
        // The waiting page handles code display for private rooms
        navigateToRoomWaiting(room);
      } else {
        // API returned success: false
        setError(res.data.error || res.data.message || 'Failed to create room');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to create room';
      setError(msg);
    } finally {
      setCreating(false);
    }
  }

  // ===================================================
  // JOIN PUBLIC ROOM
  // ===================================================
  async function handleJoinPublic(room) {
    setJoining(true);
    try {
      const res = await api.post(`/rooms/${room.id}/join`);
      const data = res.data;

      if (data.status === 'starting') {
        // Room full — navigate to game
        navigateToGame(data.gameType, data.roomId);
      } else {
        navigate(`/room-waiting/${data.roomId}`);
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to join room';
      alert(msg);
    } finally {
      setJoining(false);
    }
  }

  // ===================================================
  // JOIN PRIVATE ROOM (by code)
  // ===================================================
  async function handleJoinPrivate(e) {
    e.preventDefault();
    if (!privateCode || privateCode.length < 4) {
      alert('Enter a valid room code');
      return;
    }
    setJoining(true);
    try {
      const res = await api.post('/rooms/join-private', { code: privateCode.toUpperCase() });
      const data = res.data;

      if (data.status === 'starting') {
        navigateToGame(data.gameType, data.roomId);
      } else {
        navigate(`/room-waiting/${data.roomId}`);
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to join room';
      alert(msg);
    } finally {
      setJoining(false);
    }
  }

  // ===================================================
  // NAVIGATION HELPERS
  // ===================================================
  function navigateToGame(gameType, roomId) {
    const gamePath = `/play/${gameType}/${roomId}`;
    navigate(gamePath, {
      state: { matchSource: 'room', matchId: roomId },
    });
  }

  function navigateToRoomWaiting(room) {
    navigate(`/room-waiting/${room.id}`, {
      state: {
        roomId: room.id,
        code: room.code,
        gameType: room.gametype,
        players: room.players || [],
        playersJoined: room.players?.length || 1,
        playersNeeded: room.playercount,
        isPrivate: room.isprivate,
        entryFee: room.entryfee,
        playercount: room.playercount,
      },
    });
  }

  // ===================================================
  // COPY ROOM CODE
  // ===================================================
  function handleCopyCode() {
    if (!createdRoom?.code) return;
    navigator.clipboard.writeText(createdRoom.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  // ===================================================
  // HELPER: format fee
  // ===================================================
  function formatFee(fee) {
    const f = parseFloat(fee);
    if (!f || f <= 0) return 'Free';
    return `₦${f.toLocaleString()}`;
  }

  // ===================================================
  // STYLES
  // ===================================================
  const styles = {
    container: {
      padding: '16px',
      maxWidth: '520px',
      margin: '0 auto',
      color: '#fff',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '20px',
    },
    tabBtn: {
      minHeight: '42px',
      padding: '7px 6px',
      borderRadius: '9px',
      border: '1px solid #2a2a2a',
      background: '#111',
      color: '#888',
      fontSize: '11px',
      fontWeight: 800,
      cursor: 'pointer',
    },
    tabBtnActive: {
      color: '#000',
      background: '#00d4aa',
      borderColor: '#00d4aa',
    },
    backBtn: {
      background: 'transparent',
      border: 'none',
      color: '#00d4aa',
      fontSize: '20px',
      cursor: 'pointer',
      padding: '8px 12px 8px 0',
    },
    title: {
      fontSize: '1.3rem',
      margin: 0,
      color: '#fff',
      flex: 1,
    },
    tabs: {
      display: 'flex',
      marginBottom: '16px',
      background: '#1a1a2e',
      borderRadius: '10px',
      overflow: 'hidden',
    },
    tab: (active) => ({
      flex: 1,
      padding: '12px',
      textAlign: 'center',
      background: active ? '#00d4aa' : '#101821',
      color: active ? '#000' : '#929da5',
      border: active ? '1px solid #00d4aa' : '1px solid #3b4a55',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: active ? 600 : 500,
      transition: 'all 0.2s',
    }),
    createBtn: {
      width: '100%',
      padding: '14px',
      background: 'linear-gradient(135deg, #00d4aa, #00b894)',
      color: '#000',
      border: 'none',
      borderRadius: '12px',
      fontSize: '15px',
      fontWeight: 600,
      cursor: 'pointer',
      marginBottom: '16px',
    },
    formCard: {
      background: '#1a1a2e',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '16px',
    },
    formLabel: {
      color: '#aaa',
      fontSize: '13px',
      marginBottom: '6px',
      display: 'block',
    },
    formSelect: {
      width: '100%',
      padding: '12px',
      background: '#0d0d1a',
      color: '#fff',
      border: '1px solid #333',
      borderRadius: '8px',
      fontSize: '14px',
      marginBottom: '14px',
      WebkitAppearance: 'none',
    },
    formInput: {
      width: '100%',
      padding: '12px',
      background: '#0d0d1a',
      color: '#fff',
      border: '1px solid #333',
      borderRadius: '8px',
      fontSize: '14px',
      marginBottom: '14px',
      boxSizing: 'border-box',
    },
    toggleRow: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '14px',
    },
    toggleLabel: {
      color: '#aaa',
      fontSize: '13px',
      marginLeft: '10px',
    },
    toggle: (active) => ({
      width: '44px',
      height: '24px',
      background: active ? '#00d4aa' : '#555',
      borderRadius: '12px',
      position: 'relative',
      cursor: 'pointer',
      transition: 'background 0.2s',
    }),
    toggleKnob: (active) => ({
      width: '20px',
      height: '20px',
      background: '#fff',
      borderRadius: '50%',
      position: 'absolute',
      top: '2px',
      left: active ? '22px' : '2px',
      transition: 'left 0.2s',
    }),
    submitBtn: {
      width: '100%',
      padding: '12px',
      background: '#00d4aa',
      color: '#000',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
    },
    joinField: {
      display: 'flex',
      gap: '8px',
      marginBottom: '16px',
    },
    joinInput: {
      flex: 1,
      padding: '12px',
      background: '#0d0d1a',
      color: '#fff',
      border: '1px solid #333',
      borderRadius: '8px',
      fontSize: '18px',
      textAlign: 'center',
      letterSpacing: '4px',
      textTransform: 'uppercase',
      fontWeight: 700,
    },
    joinBtn: {
      padding: '12px 20px',
      background: '#00d4aa',
      color: '#000',
      border: 'none',
      borderRadius: '8px',
      fontWeight: 600,
      cursor: 'pointer',
    },
    roomCard: {
      width: '100%',
      boxSizing: 'border-box',
      background: '#1a1a2e',
      borderRadius: '12px',
      padding: '14px',
      marginBottom: '10px',
      cursor: 'pointer',
      border: '1px solid #2a3a45',
    },
    roomCardHover: {
      borderColor: '#00d4aa66',
    },
    roomHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px',
    },
    roomGameType: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#fff',
    },
    roomFee: {
      fontSize: '13px',
      color: '#00d4aa',
      fontWeight: 600,
    },
    roomMeta: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '12px',
      color: '#888',
    },
    modalOverlay: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
    },
    modalCard: {
      background: '#1a1a2e',
      borderRadius: '16px',
      padding: '32px',
      maxWidth: '380px',
      width: '100%',
      textAlign: 'center',
    },
    codeDisplay: {
      fontSize: '36px',
      fontWeight: 800,
      letterSpacing: '8px',
      color: '#00d4aa',
      background: '#0d0d1a',
      padding: '16px',
      borderRadius: '12px',
      margin: '16px 0',
      fontFamily: 'monospace',
    },
    copyBtn: {
      padding: '10px 24px',
      background: copied ? '#00b894' : '#00d4aa',
      color: '#000',
      border: 'none',
      borderRadius: '8px',
      fontWeight: 600,
      cursor: 'pointer',
      fontSize: '14px',
    },
    closeBtn: {
      marginTop: '12px',
      padding: '8px',
      background: 'transparent',
      color: '#888',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
    },
    emptyState: {
      textAlign: 'center',
      padding: '28px 20px',
      margin: '2px 0 16px',
      color: '#8d98a1',
      fontSize: '14px',
      background: '#121c24',
      border: '1px solid #2a3a45',
      borderRadius: '12px',
      boxShadow: '0 8px 22px rgba(0,0,0,0.16)',
    },
    emptyStateIcon: {
      fontSize: '32px',
      lineHeight: 1,
      marginBottom: '10px',
    },
    emptyStateTitle: {
      color: '#fff',
      fontSize: '15px',
      fontWeight: 600,
      marginBottom: '5px',
    },
    emptyStateMessage: {
      color: '#7f8a93',
      fontSize: '12px',
      lineHeight: 1.4,
      marginBottom: '14px',
    },
    emptyStateCreateBtn: {
      width: '100%',
      padding: '14px',
      background: 'linear-gradient(135deg, #00d4aa, #00b894)',
      color: '#000',
      border: 'none',
      borderRadius: '12px',
      fontSize: '15px',
      fontWeight: 600,
      cursor: 'pointer',
    },
  };

  // ===================================================
  // RENDER
  // ===================================================
  if (!token) return null;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate('/dashboard')} style={styles.backBtn}>← Back</button>
        <h1 style={styles.title}>Friend Rooms</h1>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 14 }}>
        <button type="button" style={{ ...styles.tabBtn, ...(activeTab === 'public' ? styles.tabBtnActive : {}) }} onClick={() => setActiveTab('public')}>My Friend Rooms</button>
        <button type="button" style={styles.tabBtn} onClick={() => navigate('/friends')}>Friends</button>
        <button type="button" style={styles.tabBtn} onClick={() => navigate('/referrals')}>Invite Friends</button>
      </div>

      {/* Create Button */}
      <button
        style={styles.createBtn}
        onClick={() => setShowCreateForm(!showCreateForm)}
      >
        {showCreateForm ? '✕ Cancel' : '+ Create Room'}
      </button>

      {/* Create Form */}
      {showCreateForm && (
        <div style={styles.formCard}>
          <form onSubmit={handleCreate}>
            <label style={styles.formLabel}>Game</label>
            <select
              style={styles.formSelect}
              value={createGameType}
              onChange={(e) => setCreateGameType(e.target.value)}
            >
              <option value="whot">🎴 Whot</option>
            </select>

            <label style={styles.formLabel}>Players</label>
            <select
              style={styles.formSelect}
              value={createPlayerCount}
              onChange={(e) => setCreatePlayerCount(parseInt(e.target.value))}
            >
              <option value={2}>2 Players (1v1)</option>
              <option value={4}>4 Players</option>
            </select>

            <label style={styles.formLabel}>Time Limit: {createTimeLimit} min</label>
            <input
              type="range"
              min="1"
              max="10"
              value={createTimeLimit}
              onChange={(e) => setCreateTimeLimit(parseInt(e.target.value))}
              style={{ width: '100%', marginBottom: '14px', accentColor: '#00d4aa' }}
            />

            <div style={styles.toggleRow}>
              <div
                style={styles.toggle(createIsPrivate)}
                onClick={() => setCreateIsPrivate(!createIsPrivate)}
              >
                <div style={styles.toggleKnob(createIsPrivate)} />
              </div>
              <span style={styles.toggleLabel}>
                {createIsPrivate ? 'Private (code required)' : 'Public (anyone can join)'}
              </span>
            </div>

            <label style={styles.formLabel}>
              Entry Fee (optional — leave empty for free)
            </label>
            <input
              type="number"
              style={styles.formInput}
              placeholder="₦0 = Free Room"
              value={createEntryFee}
              onChange={(e) => setCreateEntryFee(e.target.value)}
              min="0"
              step="100"
            />

            {error && (
              <div style={{
                color: '#ff4444', fontSize: '13px', marginBottom: '12px',
                background: 'rgba(255,68,68,0.1)', padding: '10px', borderRadius: '8px',
              }}>
                {error}
              </div>
            )}
            <button
              type="submit"
              style={{
                ...styles.submitBtn,
                opacity: creating ? 0.6 : 1,
                cursor: creating ? 'not-allowed' : 'pointer',
              }}
              disabled={creating}
            >
              {creating ? 'Creating...' : '🎮 Create Room'}
            </button>
          </form>
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          style={styles.tab(activeTab === 'public')}
          onClick={() => setActiveTab('public')}
        >
          🌐 Public Rooms
        </button>
        <button
          style={styles.tab(activeTab === 'private')}
          onClick={() => setActiveTab('private')}
        >
          🔒 Join Private
        </button>
      </div>

      {/* Tab: Public Rooms */}
      {activeTab === 'public' && (
        <div>
          {loading && <Loading variant="section" label="" />}

          {!loading && publicRooms.length === 0 && (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateIcon}>🎮</div>
              <div style={styles.emptyStateTitle}>No public rooms available</div>
              <div style={styles.emptyStateMessage}>Create one or wait for others!</div>
              <button
                type="button"
                style={styles.emptyStateCreateBtn}
                onClick={() => setShowCreateForm(true)}
              >
                + Create Room
              </button>
            </div>
          )}

          {publicRooms.map((room) => {
            const playerCount = room.players?.length || 0;
            const maxPlayers = room.playercount;
            return (
              <div
                key={room.id}
                style={styles.roomCard}
                onClick={() => handleJoinPublic(room)}
              >
                <div style={styles.roomHeader}>
                  <span style={styles.roomGameType}>
                    {GAME_ICONS[room.gametype]} {GAME_NAMES[room.gametype] || room.gametype}
                  </span>
                  <span style={styles.roomFee}>{formatFee(room.entryfee)}</span>
                </div>
                <div style={styles.roomMeta}>
                  <span>Host: {room.players?.[0]?.username || 'Unknown'}</span>
                  <span>{playerCount}/{maxPlayers} players</span>
                </div>
                <div style={{ marginTop: '10px', fontSize: '11px', color: '#7f8a93', textAlign: 'right' }}>
                  Max game time: {room.maxduration ? `${Math.floor(room.maxduration / 60)} min` : '10 min'}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tab: Join Private */}
      {activeTab === 'private' && (
        <div>
          <form onSubmit={handleJoinPrivate} style={styles.formCard}>
            <label style={{ ...styles.formLabel, textAlign: 'center' }}>
              Enter Room Code
            </label>
            <div style={styles.joinField}>
              <input
                type="text"
                style={styles.joinInput}
                value={privateCode}
                onChange={(e) => setPrivateCode(e.target.value)}
                placeholder="ABC123"
                maxLength={6}
              />
              <button
                type="submit"
                style={styles.joinBtn}
                disabled={joining}
              >
                {joining ? '...' : 'Join'}
              </button>
            </div>
            <div style={{ textAlign: 'center', fontSize: '12px', color: '#666' }}>
              Ask your friend for their room code
            </div>
          </form>
        </div>
      )}

      {/* Created Room Modal (shows code) */}
      {createdRoom && (
        <div style={styles.modalOverlay} onClick={() => setCreatedRoom(null)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: '48px', marginBottom: '8px' }}>🎮</div>
            <h2 style={{ color: '#fff', margin: '0 0 8px' }}>Room Created!</h2>
            <p style={{ color: '#888', fontSize: '13px', margin: '0 0 8px' }}>
              {GAME_ICONS[createdRoom.gametype]} {GAME_NAMES[createdRoom.gametype]} • {createdRoom.playercount}P • {formatFee(createdRoom.entryfee)}
            </p>
            {createdRoom.code && (
              <>
                <p style={{ color: '#aaa', fontSize: '12px', margin: '12px 0 4px' }}>
                  Share this code with friends:
                </p>
                <div style={styles.codeDisplay}>{createdRoom.code}</div>
                <button style={styles.copyBtn} onClick={handleCopyCode}>
                  {copied ? '✓ Copied!' : '📋 Copy Code'}
                </button>
              </>
            )}
            <div>
              <button
                style={styles.closeBtn}
                onClick={() => {
                  setCreatedRoom(null);
                  navigateToRoomWaiting(createdRoom);
                }}
              >
                Go to Room → 
              </button>
            </div>
          </div>
        </div>
      )}
      <PrimaryNav />
    </div>
  );
}
